/**
 * ContractSlice Selectors
 * 
 * Memoized selectors for accessing contract slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { ContractSliceState } from './state';

// Base selector to get the contract slice
const selectContractSlice = (state: RootState) => state.contract as unknown as ContractSliceState;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current contract entity
 */
export const selectCurrentContract = createSelector(
  selectContractSlice,
  (slice) => slice.currentContract
);

/**
 * Select the current fee schedule entity
 */
export const selectCurrentFeeSchedule = createSelector(
  selectContractSlice,
  (slice) => slice.currentFeeSchedule
);

/**
 * Select whether current contract exists
 */
export const selectHasCurrentContract = createSelector(
  selectCurrentContract,
  (contract) => contract !== null
);

/**
 * Select whether current fee schedule exists
 */
export const selectHasCurrentFeeSchedule = createSelector(
  selectCurrentFeeSchedule,
  (feeSchedule) => feeSchedule !== null
);

/**
 * Select the contract ID (if exists)
 */
export const selectContractId = createSelector(
  selectCurrentContract,
  (contract) => contract?.id
);

/**
 * Select the fee schedule ID (if exists)
 */
export const selectFeeScheduleId = createSelector(
  selectCurrentFeeSchedule,
  (feeSchedule) => feeSchedule?.id
);

// =============================================================================
// EDITING STATE SELECTORS
// =============================================================================

/**
 * Select whether user is in edit mode
 */
export const selectIsEditing = createSelector(
  selectContractSlice,
  (slice) => slice.isEditing
);

/**
 * Select whether there are unsaved changes
 */
export const selectIsDirty = createSelector(
  selectContractSlice,
  (slice) => slice.isDirty
);

/**
 * Select the currently selected tab index
 */
export const selectSelectedContractTabIndex = createSelector(
  selectContractSlice,
  (slice) => slice.selectedContractTabIndex
);

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectValidationErrors = createSelector(
  selectContractSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory selector for a specific validation error
 */
export const selectValidationErrorByField = (fieldName: string) =>
  createSelector(
    selectValidationErrors,
    (errors) => errors[fieldName]
  );

// =============================================================================
// RULE MODAL SELECTORS
// =============================================================================

/**
 * Select whether the rule modal is open
 */
export const selectRuleModalOpen = createSelector(
  selectContractSlice,
  (slice) => slice.ruleModalOpen
);

/**
 * Select the currently selected rule ID
 */
export const selectSelectedRuleId = createSelector(
  selectContractSlice,
  (slice) => slice.selectedRuleId
);

// =============================================================================
// CONTRACT FIELD SELECTORS
// =============================================================================

/**
 * Select contract company
 */
export const selectContractCompany = createSelector(
  selectCurrentContract,
  (contract) => contract?.company
);

/**
 * Select contract payer
 */
export const selectContractPayer = createSelector(
  selectCurrentContract,
  (contract) => contract?.payer
);

/**
 * Select contract centers
 */
export const selectContractCenters = createSelector(
  selectCurrentContract,
  (contract) => contract?.centers ?? []
);

/**
 * Select contract effective date
 */
export const selectContractEffectiveDate = createSelector(
  selectCurrentContract,
  (contract) => contract?.effectiveDate
);

/**
 * Select contract end date
 */
export const selectContractEndDate = createSelector(
  selectCurrentContract,
  (contract) => contract?.endDate
);

/**
 * Select contract status
 */
export const selectContractStatus = createSelector(
  selectCurrentContract,
  (contract) => contract?.status
);

/**
 * Select contract calc type
 */
export const selectContractCalcType = createSelector(
  selectCurrentContract,
  (contract) => contract?.calcType
);
